import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { Assessment } from "@mui/icons-material";
import {
  useGetMilestonesQuery,
  useGetSnagsQuery,
  useGetHandoversQuery,
  ProTrackMilestone,
  ProTrackSnag,
  ProTrackHandover,
} from "../../../redux/protrackApi";
import { useGetComsBlocksQuery, ComsBlock } from "../../../redux/comsApi";

const NAVY = "#123456";
const TODAY = new Date().toISOString().slice(0, 10);

function ProgressBar({ value, color = NAVY }: { value: number; color?: string }) {
  return (
    <Box display="flex" alignItems="center" gap={1}>
      <LinearProgress
        variant="determinate"
        value={value}
        sx={{ flex: 1, height: 8, borderRadius: 4, bgcolor: "#e0e0e0", "& .MuiLinearProgress-bar": { bgcolor: color } }}
      />
      <Typography variant="caption" fontWeight={700} color={color}>{value}%</Typography>
    </Box>
  );
}

export const ProTrackReports: React.FC = () => {
  const { data: blocks = [] } = useGetComsBlocksQuery();
  const [blockFilter, setBlockFilter] = useState("");

  const { data: milestones = [], isLoading: mlLoading } = useGetMilestonesQuery({ blockId: blockFilter || undefined });
  const { data: snags = [], isLoading: snLoading } = useGetSnagsQuery({ blockId: blockFilter || undefined });
  const { data: handovers = [], isLoading: hdLoading } = useGetHandoversQuery({ blockId: blockFilter || undefined });

  const isLoading = mlLoading || snLoading || hdLoading;

  const selectedBlocks: ComsBlock[] = blockFilter
    ? blocks.filter((b) => b.id === blockFilter)
    : blocks;

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2} mb={3}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Filter by Block</InputLabel>
          <Select label="Filter by Block" value={blockFilter} onChange={(e) => setBlockFilter(e.target.value)}>
            <MenuItem value="">All Blocks</MenuItem>
            {blocks.map((b) => <MenuItem key={b.id} value={b.id}>{b.code} — {b.name}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
      ) : selectedBlocks.length === 0 ? (
        <Box textAlign="center" py={8} color="text.secondary">
          <Assessment sx={{ fontSize: 48, opacity: 0.3 }} />
          <Typography mt={1}>No data to report.</Typography>
        </Box>
      ) : (
        <Box display="flex" flexDirection="column" gap={3}>
          {selectedBlocks.map((block) => {
            const bMilestones = milestones.filter((m: ProTrackMilestone) => m.blockId === block.id);
            const bSnags = snags.filter((s: ProTrackSnag) => s.blockId === block.id);
            const bHandovers = handovers.filter((h: ProTrackHandover) => h.blockId === block.id);

            if (!bMilestones.length && !bSnags.length && !bHandovers.length) return null;

            // Milestone analysis
            const totalM = bMilestones.length;
            const completedM = bMilestones.filter((m) => m.status === "COMPLETED").length;
            const delayedM = bMilestones.filter((m) => m.status !== "COMPLETED" && m.status !== "CANCELLED" && m.plannedEnd < TODAY).length;
            const avgProgress = totalM > 0 ? Math.round(bMilestones.reduce((s, m) => s + (m.progressPct ?? 0), 0) / totalM) : 0;

            // Snag analysis
            const totalSnags = bSnags.length;
            const openSnags = bSnags.filter((s) => s.status === "OPEN" || s.status === "IN_PROGRESS").length;
            const resolvedSnags = bSnags.filter((s) => s.status === "RESOLVED" || s.status === "VERIFIED" || s.status === "CLOSED").length;
            const criticalSnags = bSnags.filter((s) => s.severity === "CRITICAL" && (s.status === "OPEN" || s.status === "IN_PROGRESS")).length;
            const snagsByCategory = bSnags.reduce<Record<string, number>>((acc, s) => {
              acc[s.category] = (acc[s.category] ?? 0) + 1;
              return acc;
            }, {});

            // Handover analysis
            const totalH = bHandovers.length;
            const keysHanded = bHandovers.filter((h) => h.status === "KEYS_HANDED").length;
            const signedOff = bHandovers.filter((h) => h.status === "SIGNED_OFF").length;
            const handoverPct = totalH > 0 ? Math.round((keysHanded / totalH) * 100) : 0;

            return (
              <Card key={block.id} sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={700} color={NAVY} mb={0.5}>
                    {block.code} — {block.name}
                  </Typography>
                  {block.development && (
                    <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                      {block.development}
                    </Typography>
                  )}

                  <Box display="flex" flexWrap="wrap" gap={4}>
                    {/* Programme */}
                    {totalM > 0 && (
                      <Box flex="1 1 200px">
                        <Typography fontWeight={700} mb={1} fontSize={14}>Programme</Typography>
                        <ProgressBar value={avgProgress} />
                        <Box display="flex" gap={2} mt={1} flexWrap="wrap">
                          <Box>
                            <Typography variant="caption" color="text.secondary">Complete</Typography>
                            <Typography fontWeight={700} color="#2e7d32">{completedM}/{totalM}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Delayed</Typography>
                            <Typography fontWeight={700} color={delayedM > 0 ? "#f57c00" : "text.primary"}>{delayedM}</Typography>
                          </Box>
                        </Box>
                        {bMilestones.map((m) => (
                          <Box key={m.id} display="flex" alignItems="center" justifyContent="space-between" mt={0.75}>
                            <Typography variant="caption" noWrap sx={{ maxWidth: 160 }}>{m.title}</Typography>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <LinearProgress
                                variant="determinate"
                                value={m.progressPct}
                                sx={{ width: 60, height: 4, borderRadius: 2, bgcolor: "#e0e0e0", "& .MuiLinearProgress-bar": { bgcolor: NAVY } }}
                              />
                              <Typography variant="caption" fontWeight={700}>{m.progressPct}%</Typography>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    )}

                    {/* Snags */}
                    {totalSnags > 0 && (
                      <Box flex="1 1 200px">
                        <Typography fontWeight={700} mb={1} fontSize={14}>Snag List</Typography>
                        <Box display="flex" gap={2} flexWrap="wrap" mb={1}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Total</Typography>
                            <Typography fontWeight={700}>{totalSnags}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Open</Typography>
                            <Typography fontWeight={700} color={openSnags > 0 ? "#c62828" : "text.primary"}>{openSnags}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Resolved</Typography>
                            <Typography fontWeight={700} color="#2e7d32">{resolvedSnags}</Typography>
                          </Box>
                          {criticalSnags > 0 && (
                            <Box>
                              <Typography variant="caption" color="text.secondary">Critical</Typography>
                              <Typography fontWeight={700} color="#4a148c">{criticalSnags}</Typography>
                            </Box>
                          )}
                        </Box>
                        <Box display="flex" flexWrap="wrap" gap={0.5}>
                          {Object.entries(snagsByCategory).map(([cat, count]) => (
                            <Chip key={cat} label={`${cat}: ${count}`} size="small" variant="outlined" sx={{ fontSize: 10 }} />
                          ))}
                        </Box>
                      </Box>
                    )}

                    {/* Handovers */}
                    {totalH > 0 && (
                      <Box flex="1 1 200px">
                        <Typography fontWeight={700} mb={1} fontSize={14}>Handovers</Typography>
                        <ProgressBar value={handoverPct} color="#1565c0" />
                        <Box display="flex" gap={2} mt={1} flexWrap="wrap">
                          <Box>
                            <Typography variant="caption" color="text.secondary">Keys Handed</Typography>
                            <Typography fontWeight={700} color="#1b5e20">{keysHanded}/{totalH}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Signed Off</Typography>
                            <Typography fontWeight={700} color="#2e7d32">{signedOff}</Typography>
                          </Box>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}
    </Box>
  );
};
